// QuickBooks Settings — Phase 3 follow-up to the Wave D admin sub-tree.
//
// One page, twelve sections, single Save. Mirrors the section order of the
// legacy `tricivo_system/administrator/quickbooks_admin_setup.php` admin
// panel so customers migrating from the legacy system see a familiar
// surface.
//
// Form state is held locally (a single `state` object). Saving sends ONLY
// the keys that differ from the most recently fetched server snapshot —
// everything else stays in the row's existing value. The "Reset account
// cache" button calls a separate POST after a confirm modal.
//
// Admin-gated by router (RoleGate). The button-row at the top of the page
// also disables the Save / Reset buttons for safety if the gate ever
// changes; non-admins should never reach this page in practice.

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { RefreshCcw, Save } from 'lucide-react';

import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../../components/ui/ConfirmModal/ConfirmModal';
import { FormSection } from '../../../../components/form/FormSection';
import { FormRow } from '../../../../components/form/FormRow';
import { Input } from '../../../../components/form/Input';
import { Select } from '../../../../components/form/Select';
import { Checkbox } from '../../../../components/form/Checkbox';
import { Textarea } from '../../../../components/form/Textarea';
import { Tabs } from '../../../../components/ui/Tabs/Tabs';
import {
  useQbAccounts,
  useQbSettings,
  useResetQbAccounts,
  useUpsertQbSettings,
  useValidateQbSettings,
} from './hooks/useQuickBooks';
import { usePaymentModes } from '../../../bookings/hooks/useReferenceData';
import { useQbActiveModule } from './hooks/useQbActiveModule';
import { QB_MODULE_TABS, QB_MODULE_INACTIVE_NOTE } from './qbModules';
import type {
  QbExpenseCategoryMode,
  QbPaymentTypeMode,
  QbSettings,
  QbSettingsPatch,
  QbTrimDirection,
  QbVendorNaming,
} from '../../../../types/api';
import styles from './QuickBooksSettingsPage.module.css';

const DOC_NUMBER_LIMIT = 21;
const QB_ACCOUNT_LIST_ID = 'qb-account-names';

// ---- Placeholder palettes (mirror the legacy admin setup) -------------------

const LINE_TOKENS: Array<{ token: string; label: string }> = [
  { token: '{unique_id}', label: 'Unique ID' },
  { token: '{registration}', label: 'Registration' },
  { token: '{make_model}', label: 'Make / Model' },
  { token: '{chassis}', label: 'Chassis' },
  { token: '{cheque_number}', label: 'Cheque #' },
  { token: '{customer_name}', label: 'Customer name' },
  { token: '{customer_email}', label: 'Customer email' },
  { token: '{customer_phone}', label: 'Customer phone' },
  { token: '{amount}', label: 'Amount' },
  { token: '{payment_mode}', label: 'Payment mode' },
  { token: '{date}', label: 'Date' },
  { token: '{source}', label: 'Source' },
  { token: '{submitted_by}', label: 'Submitted by' },
  { token: '{product}', label: 'Product' },
  { token: '{color}', label: 'Colour' },
  { token: '{mot}', label: 'MOT' },
  { token: '{logbook}', label: 'Logbook' },
];

const DOC_TOKENS: Array<{ token: string; label: string }> = [
  { token: '{prefix}', label: 'Prefix' },
  { token: '{unique_id}', label: 'Unique ID' },
  { token: '{cheque_number}', label: 'Cheque #' },
  { token: '{registration}', label: 'Registration' },
  { token: '{date}', label: 'Date' },
  { token: '{payment_mode}', label: 'Payment mode' },
  { token: '{customer_name}', label: 'Customer name' },
];

const GROUP_DOC_TOKENS: Array<{ token: string; label: string }> = [
  { token: '{group_prefix}', label: 'Group prefix' },
  { token: '{first_id}', label: 'First ID' },
  { token: '{last_id}', label: 'Last ID' },
  { token: '{all_ids}', label: 'All IDs' },
  { token: '{count}', label: 'Count' },
  { token: '{date}', label: 'Date' },
  { token: '{txn_date}', label: 'Txn date' },
];

const SAMPLE_VALUES: Record<string, string> = {
  '{unique_id}': 'TRI-12345',
  '{registration}': 'AB12 CDE',
  '{make_model}': 'Ford Focus',
  '{chassis}': 'WF0WXXGCDW1234567',
  '{cheque_number}': 'CHQ-00842',
  '{customer_name}': 'John Smith',
  '{customer_email}': 'john@example.com',
  '{customer_phone}': '07700900000',
  '{amount}': '£850.00',
  '{payment_mode}': 'Bank Transfer',
  '{date}': '2026-05-09',
  '{source}': 'Online',
  '{submitted_by}': 'Driver A',
  '{product}': 'Vehicle Purchase',
  '{color}': 'Blue',
  '{mot}': 'Yes',
  '{logbook}': 'Yes',
};

// ---- Defaults — server returns these too, but we need a baseline ------------

const FORM_DEFAULTS: QbSettings = {
  auto_sync: false,
  sync_receipts: true,
  attach_pdf: false,
  batch_size: 50,
  cash_account_name: null,
  bank_account_name: null,
  expense_category_mode: 'auto',
  expense_category_name: null,
  income_account_name: null,
  enable_customer_ref: true,
  invoice_payment_terms: null,
  payment_type_mode: 'proper_accounting',
  custom_payment_mapping: null,
  payment_account_mapping: null,
  vendor_naming: 'customer_name',
  line_description_template: null,
  transaction_memo_template: null,
  use_unique_id_as_docnumber: false,
  docnumber_prefix: null,
  docnumber_template: null,
  docnumber_mode_overrides: null,
  unique_id_trim_direction: 'none',
  unique_id_trim_chars: 0,
  enable_departments: false,
  department_name: null,
  enable_classes: false,
  class_name: null,
  enable_job_costing: false,
  mark_billable: false,
  enable_tax_tracking: false,
  tax_code: null,
  group_docnumber_prefix: null,
  group_docnumber_template: null,
  group_memo_template: null,
  group_line_template: null,
  group_warn_mixed_modes: true,
  group_attach_all_pdfs: false,
  group_use_earliest_date: false,
  group_min_transactions: 1,
};

function renderTemplate(tmpl: string, prefix: string | null = null): string {
  if (!tmpl) return '(empty)';
  let out = tmpl;
  for (const [k, v] of Object.entries(SAMPLE_VALUES)) {
    out = out.split(k).join(v);
  }
  out = out.split('{prefix}').join(prefix ?? '');
  return out.length > 0 ? out : '(empty)';
}

function applyTrim(direction: QbTrimDirection, chars: number, sample: string): string {
  if (direction === 'none' || chars <= 0) return sample;
  if (chars >= sample.length) return '';
  if (direction === 'start') return sample.slice(chars);
  return sample.slice(0, sample.length - chars);
}

function diffPatch(prev: QbSettings, next: QbSettings): QbSettingsPatch {
  const patch: QbSettingsPatch = {};
  (Object.keys(next) as Array<keyof QbSettings>).forEach((key) => {
    const a = prev[key];
    const b = next[key];
    if (a === b) return;
    if (
      typeof a === 'object' &&
      a !== null &&
      typeof b === 'object' &&
      b !== null &&
      JSON.stringify(a) === JSON.stringify(b)
    ) {
      return;
    }
    // Cast at the assignment site — TypeScript can't narrow the union per key.
    (patch as Record<string, unknown>)[key as string] = b;
  });
  return patch;
}

// ---- Token-chip palette + textarea/input wrapper ----------------------------

interface TokenInputProps {
  id: string;
  label: string;
  helper?: string;
  value: string;
  onChange: (value: string) => void;
  tokens: Array<{ token: string; label: string }>;
  rows?: number;
  multiline?: boolean;
  maxLength?: number;
  preview?: string;
}

function TokenInput({
  id,
  label,
  helper,
  value,
  onChange,
  tokens,
  rows = 2,
  multiline = true,
  maxLength,
  preview,
}: TokenInputProps): JSX.Element {
  function insert(token: string) {
    const el = document.getElementById(id) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;
    if (!el) {
      onChange(`${value}${token}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  }

  return (
    <div className={styles.tokenField}>
      <div className={styles.tokenChips}>
        {tokens.map((t) => (
          <button
            key={t.token}
            type="button"
            className={styles.tokenChip}
            onClick={() => insert(t.token)}
            aria-label={`Insert ${t.label} placeholder`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {multiline ? (
        <Textarea
          id={id}
          label={label}
          rows={rows}
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          helper={helper}
          {...(maxLength !== undefined ? { maxLength } : {})}
        />
      ) : (
        <Input
          id={id}
          label={label}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          helper={helper}
          {...(maxLength !== undefined ? { maxLength } : {})}
        />
      )}
      {preview !== undefined ? (
        <p className={styles.preview}>
          <span className={styles.previewLabel}>Preview:</span> {preview}
        </p>
      ) : null}
    </div>
  );
}

// ---- The page itself --------------------------------------------------------

export function QuickBooksSettingsPage(): JSX.Element {
  const [module, setModule] = useQbActiveModule();
  const query = useQbSettings(module);
  const upsert = useUpsertQbSettings(module);
  const reset = useResetQbAccounts(module);
  const paymentModes = usePaymentModes();
  const accountsQuery = useQbAccounts(module, true);
  const validate = useValidateQbSettings(module);

  const [state, setState] = useState<QbSettings>(FORM_DEFAULTS);
  const [resetOpen, setResetOpen] = useState(false);
  const isInactiveModule = module !== 'collections';

  // Sync server snapshot → form on first load and on refetch.
  useEffect(() => {
    if (query.data) setState(query.data.settings);
  }, [query.data]);

  const baseline = query.data?.settings ?? FORM_DEFAULTS;
  const dirty = useMemo(
    () => Object.keys(diffPatch(baseline, state)).length > 0,
    [baseline, state],
  );

  function update<K extends keyof QbSettings>(key: K, value: QbSettings[K]): void {
    setState((s) => ({ ...s, [key]: value }));
  }

  function handleSave() {
    const patch = diffPatch(baseline, state);
    if (Object.keys(patch).length === 0) return;
    upsert.mutate(patch);
  }

  const moduleSelector = (
    <>
      <Tabs
        items={QB_MODULE_TABS}
        value={module}
        onChange={(id) => setModule(id as typeof module)}
        ariaLabel="QuickBooks module"
      />
      {isInactiveModule ? (
        <p className={styles.moduleNote} role="note">
          {QB_MODULE_INACTIVE_NOTE}
        </p>
      ) : null}
    </>
  );

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="QuickBooks Setup" />
        {moduleSelector}
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  const docnumLen = (state.docnumber_template ?? '').length;
  const docnumOver = docnumLen > DOC_NUMBER_LIMIT;
  const docnumPreview = renderTemplate(
    state.docnumber_template ?? '',
    state.docnumber_prefix,
  );
  const trimSample = 'TRI-12345';
  const trimResult = applyTrim(
    state.unique_id_trim_direction,
    state.unique_id_trim_chars,
    trimSample,
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="QuickBooks Setup"
        subtitle="Configure how transactions are synced to QuickBooks Online."
        actions={
          <>
            <Button
              variant="ghost"
              icon={RefreshCcw}
              onClick={() => setResetOpen(true)}
              loading={reset.isPending}
            >
              Reset account cache
            </Button>
            <Button
              variant="primary"
              icon={Save}
              onClick={handleSave}
              disabled={!dirty}
              loading={upsert.isPending}
            >
              Save settings
            </Button>
          </>
        }
      />

      {moduleSelector}

      <datalist id={QB_ACCOUNT_LIST_ID}>
        {(accountsQuery.data ?? []).map((a) => (
          <option key={a.id} value={a.fully_qualified_name}>
            {a.account_type}
          </option>
        ))}
      </datalist>

      <Card title="Check accounts">
        <FormSection description="Resolves every configured account against your connected QuickBooks company, exactly as a sync would. Nothing is written to QuickBooks. Save your changes first.">
          <div>
            <Button
              variant="secondary"
              onClick={() => validate.mutate()}
              loading={validate.isPending}
              disabled={dirty}
            >
              Check accounts
            </Button>
            {dirty ? <span className={styles.empty}> Save first to check the saved values.</span> : null}
          </div>
          {validate.data ? (
            <ul aria-label="Account check results">
              {validate.data.checks.map((c) => (
                <li key={c.setting}>
                  <strong>{c.ok ? 'OK' : 'Problem'}</strong> — {c.setting}:{' '}
                  {c.name === '' ? '(empty)' : c.name}
                  {c.ok ? null : <div>{c.message}</div>}
                </li>
              ))}
            </ul>
          ) : null}
        </FormSection>
      </Card>

      {/* ── Income document settings (Weighbridge + Skip) ────────────── */}
      {isInactiveModule ? (
        <Card title="Income document settings">
          <FormSection description="Weighbridge tickets and Skip jobs sync to QuickBooks as SalesReceipts (and Invoices for account-payment tickets). These fields configure those income documents.">
            <FormRow>
              <Input
                label="Income account name"
                value={state.income_account_name ?? ''}
                onChange={(e) =>
                  update('income_account_name', e.target.value === '' ? null : e.target.value)
                }
                maxLength={100}
                helper="QB item/service name used on each sales line (e.g. 'Sales'). Defaults to 'Sales'."
              />
            </FormRow>
            <Checkbox
              checked={state.enable_customer_ref}
              onChange={(e) => update('enable_customer_ref', e.target.checked)}
              label="Attach customer name to income documents"
            />
            {module === 'weighbridge' ? (
              <FormRow>
                <Input
                  label="Invoice payment terms"
                  value={state.invoice_payment_terms ?? ''}
                  onChange={(e) =>
                    update(
                      'invoice_payment_terms',
                      e.target.value === '' ? null : e.target.value,
                    )
                  }
                  maxLength={50}
                  helper="QB payment term name for account invoices (e.g. 'Net 30')."
                />
              </FormRow>
            ) : null}
          </FormSection>
        </Card>
      ) : null}

      {/* ── 1. Sync behaviour ────────────────────────────────────────── */}
      <Card title="Sync behaviour">
        <FormSection
          title="When and how often"
          description="Auto-sync runs on the 4-hour cron once enabled — there is no event-driven trigger."
        >
          <Checkbox
            checked={state.auto_sync}
            onChange={(e) => update('auto_sync', e.target.checked)}
            label="Enable auto-sync (every 4 hours)"
          />
          <Checkbox
            checked={state.sync_receipts}
            onChange={(e) => update('sync_receipts', e.target.checked)}
            label="Attach COVD receipt PDF to each expense"
          />
          <Checkbox
            checked={state.attach_pdf}
            onChange={(e) => update('attach_pdf', e.target.checked)}
            label="Attach receipt PDF to QuickBooks transaction"
          />
          <FormRow>
            <Input
              label="Batch size"
              variant="number"
              value={String(state.batch_size)}
              onChange={(e) => update('batch_size', Number(e.target.value) || 1)}
              min={1}
              max={500}
              helper="Receipts processed per sync run (1–500)."
            />
          </FormRow>
        </FormSection>
      </Card>

      {/* ── 2. Payment accounts ──────────────────────────────────────── */}
      <Card title="Payment accounts">
        <FormSection description="Exact names of the QuickBooks accounts that receive each receipt's debit. Pick from your connected QuickBooks company; a name that is not found stops the sync with a clear error (nothing is created for you).">
          <FormRow>
            <Input
              label="Cash account name"
              list={QB_ACCOUNT_LIST_ID}
              value={state.cash_account_name ?? ''}
              onChange={(e) =>
                update('cash_account_name', e.target.value === '' ? null : e.target.value)
              }
              helper="Other Current Asset account name in QB (e.g. 'Cash on Hand')."
            />
            <Input
              label="Bank account name"
              list={QB_ACCOUNT_LIST_ID}
              value={state.bank_account_name ?? ''}
              onChange={(e) =>
                update('bank_account_name', e.target.value === '' ? null : e.target.value)
              }
              helper="Bank account name in QB (e.g. 'HSBC #1234')."
            />
          </FormRow>
        </FormSection>
      </Card>

      {/* ── 3. Expense category ──────────────────────────────────────── */}
      <Card title="Expense category">
        <FormSection description="The expense account each receipt's line item is booked against.">
          <Select
            label="Mode"
            value={state.expense_category_mode}
            onChange={(e) =>
              update('expense_category_mode', e.target.value as QbExpenseCategoryMode)
            }
            options={[
              { value: 'auto', label: 'Auto-detect from QuickBooks' },
              { value: 'manual', label: 'Manual — name below' },
            ]}
          />
          {state.expense_category_mode === 'manual' ? (
            <Input
              label="Expense category name"
              list={QB_ACCOUNT_LIST_ID}
              value={state.expense_category_name ?? ''}
              onChange={(e) =>
                update(
                  'expense_category_name',
                  e.target.value === '' ? null : e.target.value,
                )
              }
              helper="Exact QB account name (e.g. 'Vehicle Purchases')."
            />
          ) : null}
        </FormSection>
      </Card>

      {/* ── 4. Payment type mapping ──────────────────────────────────── */}
      <Card title="Payment type mapping">
        <FormSection description="One row per payment mode: the QuickBooks transaction type, an optional account to debit instead of the default, and an optional reference-number template. Leave a field blank to use the default.">
          <Select
            label="Mode"
            value={state.payment_type_mode}
            onChange={(e) => update('payment_type_mode', e.target.value as QbPaymentTypeMode)}
            options={[
              {
                value: 'proper_accounting',
                label: 'Proper accounting (Cash → Cash, Bank → Check, Card → CreditCard)',
              },
              { value: 'all_expense', label: 'All as Expense' },
              { value: 'custom', label: 'Custom — per-mode mapping' },
            ]}
          />
          <PaymentModeMappingTable
            typeMode={state.payment_type_mode}
            modeNames={(paymentModes.data ?? []).map((p) => p.name)}
            custom={state.custom_payment_mapping ?? {}}
            accounts={state.payment_account_mapping ?? {}}
            docOverrides={state.docnumber_mode_overrides ?? {}}
            docPrefix={state.docnumber_prefix}
            onChangeCustom={(v) =>
              update('custom_payment_mapping', Object.keys(v).length === 0 ? null : v)
            }
            onChangeAccounts={(v) =>
              update('payment_account_mapping', Object.keys(v).length === 0 ? null : v)
            }
            onChangeDocOverrides={(v) =>
              update('docnumber_mode_overrides', Object.keys(v).length === 0 ? null : v)
            }
          />
        </FormSection>
      </Card>

      {/* ── 5. Vendor naming ─────────────────────────────────────────── */}
      <Card title="Vendor naming">
        <FormSection description="How the QB Vendor is named for each receipt.">
          <Select
            label="Naming convention"
            value={state.vendor_naming}
            onChange={(e) => update('vendor_naming', e.target.value as QbVendorNaming)}
            options={[
              { value: 'customer_name', label: 'Customer name' },
              { value: 'customer_name_vehicle', label: 'Customer name + vehicle reg' },
              { value: 'customer_email', label: 'Customer email' },
            ]}
          />
        </FormSection>
      </Card>

      {/* ── 6. Line description ──────────────────────────────────────── */}
      <Card title="Line item description">
        <FormSection description="The Description on the line item attached to each QuickBooks expense.">
          <TokenInput
            id="qb-line-template"
            label="Template"
            value={state.line_description_template ?? ''}
            onChange={(v) =>
              update('line_description_template', v === '' ? null : v)
            }
            tokens={LINE_TOKENS}
            preview={renderTemplate(state.line_description_template ?? '')}
            maxLength={500}
          />
        </FormSection>
      </Card>

      {/* ── 7. Memo / private note ───────────────────────────────────── */}
      <Card title="Memo / private note">
        <FormSection description="Internal note on the QB expense (visible in the QB transaction header).">
          <TokenInput
            id="qb-memo-template"
            label="Template"
            value={state.transaction_memo_template ?? ''}
            onChange={(v) =>
              update('transaction_memo_template', v === '' ? null : v)
            }
            tokens={LINE_TOKENS}
            preview={renderTemplate(state.transaction_memo_template ?? '')}
            maxLength={500}
          />
        </FormSection>
      </Card>

      {/* ── 8. Reference / Doc number ────────────────────────────────── */}
      <Card title="Reference / doc number">
        <FormSection description="The DocNumber field on each QuickBooks expense. QB caps this at 21 characters.">
          <Checkbox
            checked={state.use_unique_id_as_docnumber}
            onChange={(e) =>
              update('use_unique_id_as_docnumber', e.target.checked)
            }
            label="Write a doc number to each QB expense"
          />
          {state.use_unique_id_as_docnumber ? (
            <>
              <FormRow>
                <Input
                  label="Prefix"
                  value={state.docnumber_prefix ?? ''}
                  onChange={(e) =>
                    update(
                      'docnumber_prefix',
                      e.target.value === '' ? null : e.target.value,
                    )
                  }
                  maxLength={10}
                  helper="Used by the {prefix} placeholder. Max 10 chars."
                />
                <div className={styles.charCounter}>
                  <span
                    className={
                      docnumOver ? styles.charCountOver : styles.charCount
                    }
                    aria-live="polite"
                  >
                    {String(docnumLen)}/{String(DOC_NUMBER_LIMIT)}
                  </span>
                </div>
              </FormRow>
              <TokenInput
                id="qb-docnum-template"
                label="Template"
                value={state.docnumber_template ?? ''}
                onChange={(v) =>
                  update('docnumber_template', v === '' ? null : v)
                }
                tokens={DOC_TOKENS}
                multiline={false}
                maxLength={100}
                helper="Placeholders insert at cursor."
                preview={docnumPreview}
              />
              <DocnumberOverrides
                value={state.docnumber_mode_overrides ?? {}}
                onChange={(v) =>
                  update(
                    'docnumber_mode_overrides',
                    Object.keys(v).length === 0 ? null : v,
                  )
                }
              />
            </>
          ) : null}
        </FormSection>
      </Card>

      {/* ── 9. Unique ID trimming ────────────────────────────────────── */}
      <Card title="Unique ID trimming">
        <FormSection description="Trim characters off every {unique_id} placeholder before it is written to QuickBooks.">
          <FormRow>
            <Select
              label="Direction"
              value={state.unique_id_trim_direction}
              onChange={(e) =>
                update('unique_id_trim_direction', e.target.value as QbTrimDirection)
              }
              options={[
                { value: 'none', label: 'No trimming' },
                { value: 'start', label: 'Trim from start' },
                { value: 'end', label: 'Trim from end' },
              ]}
            />
            {state.unique_id_trim_direction !== 'none' ? (
              <Input
                label="Characters to trim"
                variant="number"
                value={String(state.unique_id_trim_chars)}
                onChange={(e) =>
                  update('unique_id_trim_chars', Number(e.target.value) || 0)
                }
                min={0}
                max={100}
              />
            ) : null}
          </FormRow>
          <p className={styles.preview}>
            <span className={styles.previewLabel}>Preview:</span> {trimSample} →{' '}
            <strong>{trimResult || '(empty)'}</strong>
          </p>
        </FormSection>
      </Card>

      {/* ── 10. Advanced categorisation ──────────────────────────────── */}
      <Card title="Advanced categorisation">
        <FormSection description="Departments and Classes are off by default; turning them on requires the corresponding feature to be enabled in QuickBooks.">
          <Checkbox
            checked={state.enable_departments}
            onChange={(e) => update('enable_departments', e.target.checked)}
            label="Enable departments"
          />
          {state.enable_departments ? (
            <Input
              label="Department name"
              value={state.department_name ?? ''}
              onChange={(e) =>
                update('department_name', e.target.value === '' ? null : e.target.value)
              }
            />
          ) : null}
          <Checkbox
            checked={state.enable_classes}
            onChange={(e) => update('enable_classes', e.target.checked)}
            label="Enable classes"
          />
          {state.enable_classes ? (
            <Input
              label="Class name"
              value={state.class_name ?? ''}
              onChange={(e) =>
                update('class_name', e.target.value === '' ? null : e.target.value)
              }
            />
          ) : null}
        </FormSection>
      </Card>

      {/* ── 11. Job costing & tax ────────────────────────────────────── */}
      <Card title="Job costing & tax">
        <FormSection>
          <Checkbox
            checked={state.enable_job_costing}
            onChange={(e) => update('enable_job_costing', e.target.checked)}
            label="Enable job costing (CustomerRef on each line)"
          />
          {state.enable_job_costing ? (
            <Checkbox
              checked={state.mark_billable}
              onChange={(e) => update('mark_billable', e.target.checked)}
              label="Mark each line as billable"
            />
          ) : null}
          <Checkbox
            checked={state.enable_tax_tracking}
            onChange={(e) => update('enable_tax_tracking', e.target.checked)}
            label="Enable tax tracking"
          />
          {state.enable_tax_tracking ? (
            <Input
              label="Tax code"
              value={state.tax_code ?? ''}
              onChange={(e) =>
                update('tax_code', e.target.value === '' ? null : e.target.value)
              }
              helper="QB tax-code reference (e.g. 'VAT 20')."
            />
          ) : null}
        </FormSection>
      </Card>

      {/* ── 12. Group sync ───────────────────────────────────────────── */}
      <Card title="Group sync">
        <FormSection description="When the user syncs multiple receipts together, settings here control the combined QB expense.">
          <FormRow>
            <Input
              label="Group prefix"
              value={state.group_docnumber_prefix ?? ''}
              onChange={(e) =>
                update(
                  'group_docnumber_prefix',
                  e.target.value === '' ? null : e.target.value,
                )
              }
              maxLength={20}
              helper="Used by the {group_prefix} placeholder."
            />
          </FormRow>
          <TokenInput
            id="qb-group-docnum"
            label="Group doc-number template"
            value={state.group_docnumber_template ?? ''}
            onChange={(v) =>
              update('group_docnumber_template', v === '' ? null : v)
            }
            tokens={GROUP_DOC_TOKENS}
            multiline={false}
            maxLength={100}
          />
          <TokenInput
            id="qb-group-memo"
            label="Group memo template"
            value={state.group_memo_template ?? ''}
            onChange={(v) =>
              update('group_memo_template', v === '' ? null : v)
            }
            tokens={LINE_TOKENS}
            maxLength={500}
          />
          <TokenInput
            id="qb-group-line"
            label="Group line description template"
            value={state.group_line_template ?? ''}
            onChange={(v) =>
              update('group_line_template', v === '' ? null : v)
            }
            tokens={LINE_TOKENS}
            maxLength={500}
          />
          <Checkbox
            checked={state.group_warn_mixed_modes}
            onChange={(e) => update('group_warn_mixed_modes', e.target.checked)}
            label="Warn when grouping mixed payment modes"
          />
          <Checkbox
            checked={state.group_attach_all_pdfs}
            onChange={(e) => update('group_attach_all_pdfs', e.target.checked)}
            label="Attach every grouped receipt's PDF"
          />
          <Checkbox
            checked={state.group_use_earliest_date}
            onChange={(e) => update('group_use_earliest_date', e.target.checked)}
            label="Use earliest receipt date for the QB expense"
          />
          <FormRow>
            <Input
              label="Minimum receipts to group"
              variant="number"
              value={String(state.group_min_transactions)}
              onChange={(e) =>
                update('group_min_transactions', Number(e.target.value) || 1)
              }
              min={1}
              max={50}
            />
          </FormRow>
        </FormSection>
      </Card>

      <ConfirmModal
        open={resetOpen}
        title="Reset QB account cache?"
        message="The cached QuickBooks account IDs will be cleared. Account names will be re-detected on the next sync run."
        confirmLabel="Reset cache"
        cancelLabel="Cancel"
        variant="default"
        loading={reset.isPending}
        onCancel={() => setResetOpen(false)}
        onConfirm={() =>
          reset.mutate(undefined, {
            onSuccess: () => setResetOpen(false),
          })
        }
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Custom payment mapping (Section 4 sub-table)
// ----------------------------------------------------------------------------

interface PaymentModeMappingTableProps {
  typeMode: QbPaymentTypeMode;
  /** Names of the company's real payment modes (reference data). */
  modeNames: string[];
  custom: Record<string, string>;
  accounts: Record<string, string>;
  docOverrides: Record<string, string>;
  docPrefix: string | null;
  onChangeCustom: (v: Record<string, string>) => void;
  onChangeAccounts: (v: Record<string, string>) => void;
  onChangeDocOverrides: (v: Record<string, string>) => void;
}

const QB_PAYMENT_TYPES = ['Cash', 'Check', 'CreditCard'] as const;

/** Mirrors the server's proper-accounting inference (sync.ts resolvePaymentType). */
function inferQbType(modeName: string): string {
  const lower = modeName.toLowerCase();
  if (lower.includes('cash')) return 'Cash';
  if (lower.includes('cheque') || lower.includes('check')) return 'Check';
  if (lower.includes('card') || lower.includes('credit')) return 'CreditCard';
  if (lower.includes('bank') || lower.includes('transfer')) return 'Check';
  return 'Cash';
}

function effectiveQbType(
  typeMode: QbPaymentTypeMode,
  mode: string,
  custom: Record<string, string>,
): string {
  if (typeMode === 'all_expense') return 'Cash';
  if (typeMode === 'custom') return custom[mode] ?? inferQbType(mode);
  return inferQbType(mode);
}

function withKey(
  map: Record<string, string>,
  key: string,
  value: string,
): Record<string, string> {
  const next = { ...map };
  if (value.trim() === '') delete next[key];
  else next[key] = value;
  return next;
}

function PaymentModeMappingTable({
  typeMode,
  modeNames,
  custom,
  accounts,
  docOverrides,
  docPrefix,
  onChangeCustom,
  onChangeAccounts,
  onChangeDocOverrides,
}: PaymentModeMappingTableProps): JSX.Element {
  const [newMode, setNewMode] = useState('');

  // Real payment modes first, then any names already stored in the settings
  // (e.g. a mode that was renamed or removed) so nothing is silently hidden.
  const modes = useMemo(() => {
    const set = new Set<string>(modeNames);
    for (const k of [
      ...Object.keys(custom),
      ...Object.keys(accounts),
      ...Object.keys(docOverrides),
    ]) {
      set.add(k);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [modeNames, custom, accounts, docOverrides]);

  function removeMode(mode: string) {
    onChangeCustom(withKey(custom, mode, ''));
    onChangeAccounts(withKey(accounts, mode, ''));
    onChangeDocOverrides(withKey(docOverrides, mode, ''));
  }

  function addMode() {
    const trimmed = newMode.trim();
    if (trimmed === '' || modes.includes(trimmed)) return;
    onChangeCustom({ ...custom, [trimmed]: inferQbType(trimmed) });
    setNewMode('');
  }

  const typeEditable = typeMode === 'custom';

  return (
    <div className={styles.mappingTableWrap}>
      <table className={styles.mappingTable}>
        <thead>
          <tr>
            <th>Payment mode</th>
            <th>QB transaction type</th>
            <th>Account to debit</th>
            <th>Reference number template</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {modes.length === 0 ? (
            <tr>
              <td colSpan={5} className={styles.empty}>
                No payment modes yet — add one below.
              </td>
            </tr>
          ) : (
            modes.map((mode) => {
              const docValue = docOverrides[mode] ?? '';
              const isStored =
                custom[mode] !== undefined ||
                accounts[mode] !== undefined ||
                docOverrides[mode] !== undefined;
              return (
                <tr key={mode}>
                  <td>{mode}</td>
                  <td>
                    <select
                      aria-label={`QB type for ${mode}`}
                      value={effectiveQbType(typeMode, mode, custom)}
                      disabled={!typeEditable}
                      onChange={(e) => onChangeCustom({ ...custom, [mode]: e.target.value })}
                    >
                      {QB_PAYMENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      aria-label={`Account override for ${mode}`}
                      list={QB_ACCOUNT_LIST_ID}
                      value={accounts[mode] ?? ''}
                      onChange={(e) => onChangeAccounts(withKey(accounts, mode, e.target.value))}
                      placeholder="(use default)"
                    />
                  </td>
                  <td>
                    <input
                      aria-label={`Reference template for ${mode}`}
                      value={docValue}
                      maxLength={100}
                      onChange={(e) =>
                        onChangeDocOverrides(withKey(docOverrides, mode, e.target.value))
                      }
                      placeholder="(use default template)"
                    />
                    {docValue !== '' ? (
                      <div className={styles.empty}>
                        Preview: {renderTemplate(docValue, docPrefix)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {isStored ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMode(mode)}
                        aria-label={`Clear overrides for ${mode}`}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {!typeEditable ? (
        <p className={styles.empty}>
          Transaction types are fixed by the mode above. Choose &ldquo;Custom&rdquo; to set them per
          payment mode. Account and reference overrides always apply.
        </p>
      ) : null}
      <p className={styles.empty}>
        Reference tokens: {DOC_TOKENS.map((t) => t.token).join(' ')}
      </p>
      <div className={styles.addRow}>
        <Input
          aria-label="New payment mode"
          value={newMode}
          onChange={(e) => setNewMode(e.target.value)}
          placeholder="Other payment mode name (must match the receipt's mode)"
        />
        <Button variant="secondary" onClick={addMode}>
          Add mode
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Per-payment-mode doc-number overrides (Section 8 sub-table)
// ----------------------------------------------------------------------------

interface DocnumberOverridesProps {
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}

function DocnumberOverrides({ value, onChange }: DocnumberOverridesProps): JSX.Element {
  const [newMode, setNewMode] = useState('');

  function set(mode: string, tmpl: string) {
    const next = { ...value };
    if (tmpl === '') delete next[mode];
    else next[mode] = tmpl;
    onChange(next);
  }

  function remove(mode: string) {
    const next = { ...value };
    delete next[mode];
    onChange(next);
  }

  function add() {
    const m = newMode.trim();
    if (m === '' || value[m] !== undefined) return;
    onChange({ ...value, [m]: '' });
    setNewMode('');
  }

  const entries = Object.entries(value);

  return (
    <div className={styles.overrides}>
      <h4 className={styles.overridesHeading}>Per-mode doc-number overrides</h4>
      <p className={styles.overridesHint}>
        Override the doc-number template for specific payment modes. Leave a row blank to use the default template.
      </p>
      {entries.length === 0 ? (
        <p className={styles.empty}>No overrides yet.</p>
      ) : (
        <ul className={styles.overridesList}>
          {entries.map(([mode, tmpl]) => (
            <li key={mode}>
              <span className={styles.overridesMode}>{mode}</span>
              <input
                aria-label={`Doc-number template for ${mode}`}
                value={tmpl}
                onChange={(e) => set(mode, e.target.value)}
              />
              <Button variant="ghost" size="sm" onClick={() => remove(mode)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.addRow}>
        <Input
          aria-label="New override mode"
          value={newMode}
          onChange={(e) => setNewMode(e.target.value)}
          placeholder="Payment mode (e.g. Cheque)"
        />
        <Button variant="secondary" onClick={add}>
          Add override
        </Button>
      </div>
    </div>
  );
}
